import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Box,
	Title,
	Text,
	TextInput,
	Select,
	Button,
	Group,
} from '@mantine/core'
import {
	getExperiencePositions,
	updateStaffPosition,
	addStaffPosition,
	getStationOptions,
} from '../services/actions/experienceActions'
import { getOrgUsers } from '../services/actions/orgActions'

const EditStaffPage = () => {
	const { experienceSlug, staffId, orgSlug } = useParams()
	const navigate = useNavigate()

	const [user, setUser] = useState(null)
	const [position, setPosition] = useState(null)
	const [level, setLevel] = useState('staff')
	const [positionName, setPositionName] = useState('Staff')
	const [stationOptions, setStationOptions] = useState([])
	const [station, setStation] = useState('')
	const [error, setError] = useState('')

	useEffect(() => {
		const fetchData = async () => {
			try {
				const users = await getOrgUsers(orgSlug)
				const positions = await getExperiencePositions(experienceSlug, orgSlug)
				const foundUser = users.find((u) => u.id === staffId)
				const foundPosition = positions.find((p) => p.userId === staffId)
				const stations = await getStationOptions(experienceSlug, orgSlug)

				if (!foundUser) {
					setError('User not found')
					return
				}

				setUser(foundUser)
				setPosition(foundPosition)
				setLevel(foundPosition?.level || 'staff')
				setPositionName(foundPosition?.positionName || 'Staff')
				setStationOptions(stations)
				setStation(foundPosition?.station || '')
			} catch (err) {
				console.error('Failed to fetch user/staff:', err)
				setError('Failed to load user')
			}
		}

		if (orgSlug) {
			fetchData()
		}
	}, [experienceSlug, staffId, orgSlug])

	const handleSave = async () => {
		try {
			if (position) {
				await updateStaffPosition(experienceSlug, orgSlug, position.id, {
					level,
					station,
					positionName,
				})
			} else {
				await addStaffPosition({
					orgSlug,
					userId: staffId,
					experienceSlug,
					level,
					station,
					positionName,
				})
			}
			navigate(`/${orgSlug}/experiences/${experienceSlug}/manage/staff`)
		} catch (err) {
			console.error('Failed to save staff position:', err)
			setError('Failed to save changes')
		}
	}

	if (error) return <Text color='red'>{error}</Text>
	if (!user) return <Text>Loading...</Text>

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				{position ? 'Edit Staff' : 'Add Staff'}
			</Title>

			<Text>
				<strong>Name:</strong> {user.name}
			</Text>
			<Text mb='sm'>
				<strong>Email:</strong> {user.email}
			</Text>

			<Select
				label='Position Level'
				value={level}
				onChange={(value) => {
					setLevel(value)
					if (
						!positionName ||
						positionName === 'Staff' ||
						positionName === 'Owner'
					) {
						setPositionName(value === 'owner' ? 'Owner' : 'Staff')
					}
				}}
				data={[
					{ value: 'staff', label: 'Staff' },
					{ value: 'owner', label: 'Owner' },
				]}
				mb='sm'
			/>

			<TextInput
				label='Position Name'
				value={positionName}
				onChange={(e) => setPositionName(e.target.value)}
				mb='sm'
			/>

			<Select
				label='Station'
				placeholder='Select a station'
				value={station}
				onChange={setStation}
				data={stationOptions.map((s) => ({
					value: s.id,
					label: s.name,
				}))}
				mb='md'
				searchable
				clearable
			/>

			<Group mt='md'>
				<Button onClick={handleSave}>Save</Button>
				<Button
					variant='light'
					color='gray'
					onClick={() =>
						navigate(`/${orgSlug}/experiences/${experienceSlug}/manage/staff`)
					}
				>
					Cancel
				</Button>
			</Group>
		</Box>
	)
}

export default EditStaffPage
